import {EmptyState} from "../../../../components/kit";
import { Alert, Box, Grid, IconButton, List, ListItem, ListItemText, Tab, Typography } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {H4, Typographies} from "../../../../components/utils/typography_consts";
import {TabContext, TabList, TabPanel} from "@mui/lab";
import React, {useState, useEffect} from "react";
import {Occupation_calendar} from "./occupation_calendar";
import {Occupation, OccupationPersonMonth,} from "arteco-api-client-ts";
import DedicationTable from "./dedication/dedication_table";


import ListItemButton from "@mui/material/ListItemButton";


export interface OccupationIndividualProps {
	occupation: Occupation;
	month: string;
	year: number;
}

export const OccupationIndividual = ({occupation, month, year}: OccupationIndividualProps) => {

	const [selectedTab, setSelectedTab] = useState<string>("dedication");
	const [selectedPerson, setSelectedPerson] = useState<OccupationPersonMonth | undefined>(undefined);

	useEffect(() => {
		if (selectedPerson) {
			const peopleInMonth = occupation.peopleByMonth?.[month] || [];
			const stillExists = peopleInMonth.find(p => p.personId === selectedPerson.personId);
			setSelectedPerson(stillExists);
		}
	}, [month, occupation]);

	const getWorkedHours = (personId: number | undefined): number => {
		return occupation?.planningByMonthAndPersonId?.[month]?.[personId?.toString() || ""]?.workedHours || 0;
	};

	const hasImputedData = (occupation.monthsWithImputedData || []).includes(Number(month));

	/**
	 * Planificado frente a **dedicado**, para localizar en la propia lista a quien tiene horas sin
	 * repartir. Se compara contra las dedicaciones y no contra lo imputado en Redmine porque lo
	 * segundo es la materia prima: quien no usa Redmine saldría a cero teniéndolo todo repartido.
	 */
	const hoursOf = (person: OccupationPersonMonth) => {
		const round2 = (n: number) => Math.round(n * 100) / 100;
		const planned = round2(person.availableHours || 0);
		const dedicated = round2(person.workedHours || 0);
		const imputed = round2(person.imputedHours || 0);
		return {planned, dedicated, imputed, mismatch: Math.abs(dedicated - planned) >= 0.01};
	};

	return (
        <Grid container wrap={"nowrap"} spacing={3}>
            <Grid item sm={4}>
				<div style={{
					maxHeight: "70vh",
					overflow: "auto",
					backgroundColor: "rgba(243,243,243,0.37)",
					borderRight: "1px solid gray",
					borderBottom: "1px solid gray"
				}}>
					<List>
						{occupation.peopleByMonth && (
							<>
								{(occupation.peopleByMonth[month] || []).map((occupationSummary, pIdx) => (
									<ListItem
                                        key={pIdx}
                                        divider
                                        disablePadding
                                        secondaryAction={
											<IconButton style={{backgroundColor: "white"}} onClick={() => {
												window.open("/app/admin/employee/person/" + occupationSummary.personId);
											}} edge="end" aria-label="ver perfil de empleado">
												<PersonIcon/>
											</IconButton>
										}>
									<ListItemButton
                                        selected={selectedPerson?.personId == occupationSummary.personId}
                                        onClick={() => setSelectedPerson(occupationSummary)}>
										{/* Nombre arriba y cifras debajo: en una sola línea las cifras se
										    comían el nombre en cuanto la columna se estrechaba. */}
										<ListItemText
											primaryTypographyProps={{noWrap: true}}
											primary={
												<>
													{pIdx + 1 + " - "}
													<span
														style={{color: ((occupationSummary.availableHours || 0) > getWorkedHours(occupationSummary.personId)) ? "red" : "inherit"}}>
														{occupationSummary.personName}
													</span>
													<small style={{color: "gray", marginLeft: "0.5em"}}>
														{occupationSummary.personHelp}
													</small>
												</>
											}
											secondary={
												<span style={{display: "flex", gap: "1em", fontSize: "0.75rem"}}>
													{(() => {
														const h = hoursOf(occupationSummary);
														const imputedNote = hasImputedData
															? ` — imputado en Redmine: ${h.imputed} h`
															: "";
														return (
															<span title={"Planificado / dedicado" + imputedNote}
																  style={{
																	  color: h.mismatch ? "#a15c00" : "gray",
																	  fontWeight: h.mismatch ? 600 : "inherit"
																  }}>
																{h.planned} / {h.dedicated} h
															</span>
														);
													})()}
													{/* El salario solo llega a dirección y RR. HH. (#14002): sin él, solo la tarifa */}
													{occupationSummary.salary == null ? (
														<span title={"Precio hora"} style={{color: "gray"}}>
															{(occupationSummary.hourlyRate || 0)} €/h
														</span>
													) : (
														<span title={"Precio hora / salario anual"}
															  style={{
																  color: ((occupationSummary.hourlyRate || 0) <= occupationSummary.salary / 1000) ? "red" : "gray"
															  }}>
															{(occupationSummary.hourlyRate || 0)} / {(occupationSummary.salary / 1000).toFixed(1)}K
														</span>
													)}
												</span>
											}/>
									</ListItemButton>
								</ListItem>
								))}
							</>
						)}

					</List>
				</div>
			</Grid>
            <Grid item sm sx={{padding: 1, backgroundColor: "white", zIndex: 10}}>
				{!selectedPerson && (
					<EmptyState icon={<ArrowBackIcon/>} title="Selecciona una persona"
								description="Verás sus dedicaciones del mes y su calendario."/>
				)}
				{selectedPerson && (
					<>

						<TabContext value={selectedTab}>
							<Box sx={{borderBottom: 1, borderColor: 'divider'}}>
								<TabList onChange={(event: React.SyntheticEvent, newValue: string) => {
									setSelectedTab(newValue);
								}}>
									<Tab label="Dedicación" value="dedication"/>
									<Tab label="Calendario" value="calendar"/>
								</TabList>
							</Box>
							<TabPanel value="dedication" sx={{padding: 0, paddingTop: 1}}>
								<DedicationTable showPerson={false}
												 personId={selectedPerson.personId}
												 occupation={occupation}
												 month={month}
												 year={year}/>

							</TabPanel>
							<TabPanel value="calendar" sx={{padding: 0, paddingTop: 1}}>
								<Occupation_calendar month={month}
													 occupation={occupation}
													 personId={selectedPerson.personId as number}/>
							</TabPanel>
						</TabContext>

					</>
				)}
			</Grid>
        </Grid>
    );
}